import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { usePricing, applyDiscount, formatPrice } from "@/lib/pricing";
import { ArrowLeft, ArrowRight, CheckCircle, Phone, MessageCircle, Star, Truck, Shield, Clock, MapPin } from "lucide-react";

const BuyukcekmecePage = () => {
  const { prices, discountRate, ready } = usePricing(); // fiyatlar /api/pricing\'den (sabit fiyat yok)
  const handleWhatsApp = () => {
    window.open("https://wa.me/902126990301?text=Merhaba%2C%20B%C3%BCy%C3%BCk%C3%A7ekmece%27de%20hal%C4%B1%20y%C4%B1kama%20hizmeti%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.", "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Büyükçekmece Halı Yıkama Hizmeti | Halıyol</title>
        <meta name="description" content="Büyükçekmece'de profesyonel halı yıkama hizmeti. Kapıdan alım teslim, uygun fiyat, hızlı teslimat. Hemen sipariş oluşturun!" />
        <meta name="keywords" content="büyükçekmece halı yıkama, büyükçekmece halı temizleme, büyükçekmece kapıdan halı yıkama, halı yıkama büyükçekmece fiyat" />
        <link rel="canonical" href="https://www.haliyol.com/blog/buyukcekmeceye-hali-yikama" />
        <meta property="og:title" content="Büyükçekmece Halı Yıkama Hizmeti | Halıyol" />
        <meta property="og:description" content="Büyükçekmece'de profesyonel halı yıkama hizmeti. Kapıdan alım teslim, uygun fiyat, hızlı teslimat." />
        <meta property="og:url" content="https://www.haliyol.com/blog/buyukcekmeceye-hali-yikama" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1527515545081-5db817172677?w=1200" />
      </Helmet>

      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-slate-600 hover:text-orange-500 transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Bloglara Geri Dön
          </Link>
          <Button onClick={handleWhatsApp} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 gap-2">
            <MessageCircle className="w-4 h-4" />
            WhatsApp'tan Sor
          </Button>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
          <MapPin className="w-4 h-4" />
          <span>Büyükçekmece</span>
          <span>•</span>
          <span>Halı Yıkama</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-800 mb-4 leading-tight">
          Büyükçekmece Halı Yıkama – Kapıdan Alım Teslim Hizmeti
        </h1>
        <p className="text-xl text-slate-500 mb-8">
          Büyükçekmece'de adresinizden alıyor, profesyonelce yıkayıp tertemiz teslim ediyoruz.
        </p>

        <div className="rounded-2xl overflow-hidden mb-8 aspect-[16/7]">
          <img
            src="https://images.unsplash.com/photo-1527515545081-5db817172677?w=1200&q=85"
            alt="Büyükçekmece halı yıkama hizmeti"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {["büyükçekmece halı yıkama", "kapıdan halı yıkama", "profesyonel temizlik", "halı temizliği"].map((tag, i) => (
            <span key={i} className="text-xs bg-orange-50 text-orange-600 px-3 py-1 rounded-full font-medium">{tag}</span>
          ))}
        </div>

        <div className="prose max-w-none text-slate-600 text-lg leading-relaxed space-y-6">

          <h2 className="text-3xl font-bold text-slate-800">Büyükçekmece'de Halı Yıkama Hizmeti</h2>
          <p>
            Büyükçekmece, İstanbul'un batı yakasında göl kıyısında yer alan güzel bir ilçedir. Deniz ve göl havasının yarattığı nem ortamı halılarda küf ve bakteri oluşumunu hızlandırabilir. Bu nedenle Büyükçekmece'de düzenli halı yıkama ayrı bir önem taşır.
          </p>
          <p>
            Halıyol olarak Büyükçekmece'nin tüm mahallelerinde — Karaağaç, Kumburgaz, Mimarsinan, Pınarhisar, Tepecik ve çevresinde — kapıdan alım teslim halı yıkama hizmeti sunuyoruz.
          </p>

          <h2 className="text-3xl font-bold text-slate-800">Büyükçekmece Halı Yıkama Sürecimiz</h2>

          <div className="not-prose grid md:grid-cols-3 gap-4 my-8">
            {[
              { step: "1", title: "Sipariş Ver", desc: "Online sipariş veya WhatsApp ile başlayın" },
              { step: "2", title: "Halı Alınır", desc: "Büyükçekmece adresinizden ücretsiz alım" },
              { step: "3", title: "Temiz Teslim", desc: "3 günde tertemiz halınız kapınızda" },
            ].map((item, i) => (
              <div key={i} className="bg-orange-50 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">{item.step}</div>
                <h3 className="font-bold text-slate-800 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>

          <h2 className="text-3xl font-bold text-slate-800">Büyükçekmece Halı Yıkama Fiyatları 2026</h2>

          <div className="not-prose grid md:grid-cols-2 gap-3 my-6">
            {[
              { type: "Makine Halısı", key: "makine" },
              { type: "Shaggy Halı", key: "shaggy" },
              { type: "Bambu Halı", key: "bambu" },
              { type: "Yün Halı", key: "yun" },
              { type: "İpek Halı", key: "ipek" },
              { type: "El Dokuma", key: "el_dokuma" },
              { type: "Kilim", key: "kilim" },
              { type: "Viskon Halı", key: "viskon" },
            ].map((item, i) => (
              <div key={i} className="flex justify-between items-center bg-slate-50 rounded-xl px-5 py-3 border border-slate-100">
                <span className="font-medium text-slate-700">{item.type}</span>
                <span className="font-bold text-orange-500">
                  {ready && prices?.[item.key] !== undefined ? (
                    <>
                      <span className="text-xs font-normal line-through text-slate-400 mr-2">{formatPrice(prices[item.key])}</span>
                      {formatPrice(applyDiscount(prices[item.key], discountRate))} ₺/m²
                    </>
                  ) : "—"}
                </span>
              </div>
            ))}
          </div>
          <h2 className="text-3xl font-bold text-slate-800">Neden Halıyol?</h2>
          <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
            {[
              { icon: <Truck className="w-5 h-5" />, text: "Büyükçekmece'de ücretsiz kapıdan alım" },
              { icon: <Clock className="w-5 h-5" />, text: "Ortalama 3 gün içinde teslim" },
              { icon: <Shield className="w-5 h-5" />, text: "Sigortalı taşıma güvencesi" },
              { icon: <Star className="w-5 h-5" />, text: "Nem ve küfe karşı özel yıkama" },
              { icon: <CheckCircle className="w-5 h-5" />, text: "Şeffaf fiyat, gizli ücret yok" },
              { icon: <Phone className="w-5 h-5" />, text: "WhatsApp ile anında destek" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-green-50 rounded-xl px-5 py-4 border border-green-100">
                <span className="text-green-500 flex-shrink-0">{item.icon}</span>
                <span className="text-slate-700 font-medium">{item.text}</span>
              </div>
            ))}
          </div>

          <h2 className="text-3xl font-bold text-slate-800">Sık Sorulan Sorular</h2>
          <div className="not-prose space-y-4 my-6">
            {[
              { q: "Büyükçekmece'nin hangi mahallelerinde hizmet veriyorsunuz?", a: "Karaağaç, Kumburgaz, Mimarsinan, Pınarhisar, Tepecik ve tüm Büyükçekmece mahallelerinde hizmet veriyoruz." },
              { q: "Nem çeken halılar için özel yıkama yapıyor musunuz?", a: "Evet, deniz ve göl kenarındaki evlerde nem sorunu yaşayan halılar için özel yıkama ve kurutma uyguluyoruz." },
              { q: "Halı yıkama kaç günde tamamlanır?", a: "Ortalama 3 iş günü içinde halınız temizlenip adresinize teslim edilir." },
              { q: "Ödeme ne zaman yapılır?", a: "Halınız teslim edildiğinde ödeme yapabilirsiniz." },
            ].map((item, i) => (
              <div key={i} className="border border-slate-100 rounded-xl p-5">
                <h3 className="font-bold text-slate-800 mb-2">❓ {item.q}</h3>
                <p className="text-slate-600 text-sm">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-10 text-center text-white">
          <h2 className="text-3xl font-bold mb-3">Büyükçekmece'de Halı Yıkatmaya Hazır mısınız?</h2>
          <p className="text-orange-100 mb-8 text-lg">Hemen sipariş oluşturun, ekibimiz sizinle iletişime geçsin.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="tel:02126990301">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg font-bold">
                <Phone className="w-5 h-5 mr-2" /> Hemen Ara
              </Button>
            </a>
            <Button variant="outline" onClick={handleWhatsApp} className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg gap-2">
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </Button>
          </div>
        </div>

        <div className="mt-12">
          <h3 className="text-xl font-bold text-slate-800 mb-4">Diğer İlçeler</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { slug: "beylikduzu-hali-yikama", title: "Beylikdüzü Halı Yıkama" },
              { slug: "esenyurt-hali-yikama", title: "Esenyurt Halı Yıkama" },
              { slug: "avcilar-hali-yikama", title: "Avcılar Halı Yıkama" },
            ].map((item, i) => (
              <Link key={i} to={`/blog/${item.slug}`} className="bg-orange-50 hover:bg-orange-100 rounded-xl p-4 text-center font-medium text-orange-600 transition-colors">
                {item.title} →
              </Link>
            ))}
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-8 px-4 text-center mt-10">
        <p className="text-slate-400 text-sm">&copy; 2026 HALIYOL. Tüm hakları saklıdır.</p>
      </footer>
    </div>
  );
};

export default BuyukcekmecePage;
